import { DEG, TAU, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { RootsValues } from './params';

/**
 * Root network growing outward from the letterforms.
 *
 * The counterpart to the tree: instead of hanging the glyphs on a structure, the
 * structure grows *out of* them. Each strand starts on the contour, heading along the
 * outward normal, and is steered away from the text as it grows — so the roots never
 * cross a letter, which is what keeps the type readable underneath.
 */
export interface RootSegment {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  width: number;
  depth: number;
  distance: number;
}

export interface RootsState {
  segments: RootSegment[];
  /** Growth frontier: position, heading, length, depth, width. */
  frontier: { x: number; y: number; angle: number; length: number; depth: number; width: number }[];
  palette: ResolvedPalette;
  levelsDone: number;
  targetDepth: number;
  maxDepth: number;
}

function values(ctx: TechniqueContext): RootsValues {
  return ctx.values as unknown as RootsValues;
}

export function init(ctx: TechniqueContext): RootsState {
  const v = values(ctx);
  const samples = ctx.field.sampleOutline(Math.max(4, v.strands));

  const frontier = samples.map((s) => ({
    x: s.x + s.nx * 1.5,
    y: s.y + s.ny * 1.5,
    angle: Math.atan2(s.ny, s.nx),
    length: v.segmentLength,
    depth: 0,
    width: v.baseWidth,
  }));

  return {
    segments: [],
    frontier,
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    levelsDone: 0,
    targetDepth: v.depth,
    maxDepth: Math.max(1, v.depth),
  };
}

export function step(state: RootsState, _dt: number, ctx: TechniqueContext): RootsState {
  if (state.levelsDone >= state.targetDepth || state.frontier.length === 0) {
    state.frontier = [];
    return state;
  }
  const v = values(ctx);
  const next: typeof state.frontier = [];

  // A hard cap: five children over twelve levels is exponential.
  const MAX_SEGMENTS = 400000;

  for (const node of state.frontier) {
    if (state.segments.length >= MAX_SEGMENTS) break;
    const count = state.levelsDone === 0 ? 1 : v.children;

    for (let i = 0; i < count; i++) {
      const spread = count === 1 ? 0 : (i / (count - 1)) * 2 - 1;
      let angle = node.angle + spread * v.angle * DEG + ctx.rng.signed(v.jitter * DEG);

      // Directional bias, blended towards straight down (or up).
      if (v.gravity !== 0) {
        const target = v.gravity > 0 ? Math.PI / 2 : -Math.PI / 2;
        const delta = ((target - angle + Math.PI * 3) % TAU) - Math.PI;
        angle += delta * Math.abs(v.gravity) * 0.3;
      }

      // Steer away from the glyphs so a root never runs through a letter.
      if (v.repel > 0) {
        const signed = ctx.field.distance(node.x, node.y);
        if (signed < v.segmentLength * 2) {
          const [gx, gy] = ctx.field.gradient(node.x, node.y);
          if (gx !== 0 || gy !== 0) {
            const away = Math.atan2(gy, gx);
            const delta = ((away - angle + Math.PI * 3) % TAU) - Math.PI;
            const falloff = 1 - clamp01(signed / (v.segmentLength * 2));
            angle += delta * clamp01(falloff * v.repel);
          }
        }
      }

      const x1 = node.x + Math.cos(angle) * node.length;
      const y1 = node.y + Math.sin(angle) * node.length;

      // Never enter the letterforms.
      if (ctx.field.isInside(x1, y1)) continue;
      if (x1 < -60 || y1 < -60 || x1 > ctx.width + 60 || y1 > ctx.height + 60) continue;

      const width = Math.max(0.2, node.width * v.taper);
      state.segments.push({
        x0: node.x,
        y0: node.y,
        x1,
        y1,
        width,
        depth: state.levelsDone + 1,
        distance: ctx.field.distance(x1, y1),
      });

      if (state.levelsDone + 1 < state.targetDepth) {
        next.push({ x: x1, y: y1, angle, length: node.length * v.lengthRatio, depth: state.levelsDone + 1, width });
      }
    }
  }

  state.frontier = next;
  state.levelsDone++;
  return state;
}

export function isComplete(state: RootsState): boolean {
  return state.levelsDone >= state.targetDepth || state.frontier.length === 0;
}

export function draw(state: RootsState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const reach = Math.max(1, ctx.field.maxOutside);
  c.lineCap = 'round';

  for (const segment of state.segments) {
    let t: number;
    switch (v.colorMode) {
      case 'depth':
        t = segment.depth / state.maxDepth;
        break;
      case 'distance':
        t = clamp01(Math.abs(segment.distance) / reach);
        break;
      default:
        t = 0;
    }
    c.strokeStyle = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
    c.lineWidth = segment.width;
    c.beginPath();
    c.moveTo(segment.x0, segment.y0);
    c.lineTo(segment.x1, segment.y1);
    c.stroke();
  }
}

export function elementCount(state: RootsState): number {
  return state.segments.length;
}

export function toSVG(state: RootsState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');
  const reach = Math.max(1, ctx.field.maxOutside);
  const parts: string[] = [`<g fill="none" stroke-linecap="round" stroke-opacity="${v.opacity}">`];

  for (const segment of state.segments) {
    let t: number;
    switch (v.colorMode) {
      case 'depth':
        t = segment.depth / state.maxDepth;
        break;
      case 'distance':
        t = clamp01(Math.abs(segment.distance) / reach);
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0) : state.palette.at(t);
    parts.push(
      `<line x1="${f(segment.x0)}" y1="${f(segment.y0)}" x2="${f(segment.x1)}" y2="${f(segment.y1)}" stroke="${color}" stroke-width="${f(segment.width)}"/>`,
    );
  }

  parts.push('</g>');
  return parts.join('\n');
}
